// Sound Manager with Techno Music Playlist & Enhanced SFX
// Uses Web Audio API for all synthesis - no external audio files needed

class SoundManager {
  constructor() {
    this.audioContext = null;
    this.masterGain = null;
    this.musicGain = null;
    this.sfxGain = null;
    this.musicPlaying = false;
    this.currentTrack = -1;
    this.musicTimeouts = [];
    this.musicNodes = [];
    this.initialized = false;
    this.muted = false;
  }

  init() {
    if (this.initialized) return;
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.masterGain = this.audioContext.createGain();
      this.masterGain.connect(this.audioContext.destination);
      this.masterGain.gain.value = 0.5;
      this.musicGain = this.audioContext.createGain();
      this.musicGain.connect(this.masterGain);
      this.musicGain.gain.value = 0.25;
      this.sfxGain = this.audioContext.createGain();
      this.sfxGain.connect(this.masterGain);
      this.sfxGain.gain.value = 0.6;
      this.initialized = true;
    } catch (e) {
      console.warn('Web Audio API not supported:', e);
    }
  }

  resume() {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
  }

  // === SFX ===

  playShoot() {
    if (!this.initialized) return;
    this.resume();
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    osc.connect(gain); gain.connect(this.sfxGain);
    osc.type = 'square';
    osc.frequency.setValueAtTime(880, this.audioContext.currentTime);
    osc.frequency.exponentialRampToValueAtTime(220, this.audioContext.currentTime + 0.1);
    gain.gain.setValueAtTime(0.3, this.audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.1);
    osc.start(); osc.stop(this.audioContext.currentTime + 0.1);
  }

  playAssaultRifle() {
    // Punchy "pop" — short noise burst + body click. Cadence comes from the firing
    // rate itself (called once per round @ 130ms cooldown), giving a natural pop-pop-pop.
    if (!this.initialized) return;
    this.resume();
    const t = this.audioContext.currentTime;
    // 1) Noise burst (sharp report)
    const bufLen = Math.floor(this.audioContext.sampleRate * 0.04);
    const buf = this.audioContext.createBuffer(1, bufLen, this.audioContext.sampleRate);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) ch[i] = (Math.random() * 2 - 1);
    const noise = this.audioContext.createBufferSource();
    noise.buffer = buf;
    const hp = this.audioContext.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.setValueAtTime(900, t);
    const noiseGain = this.audioContext.createGain();
    noiseGain.gain.setValueAtTime(0.18, t);
    noiseGain.gain.exponentialRampToValueAtTime(0.01, t + 0.05);
    noise.connect(hp); hp.connect(noiseGain); noiseGain.connect(this.sfxGain);
    noise.start(t); noise.stop(t + 0.06);

    // 2) Body click — short tonal blip
    const osc = this.audioContext.createOscillator();
    const oscGain = this.audioContext.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(420, t);
    osc.frequency.exponentialRampToValueAtTime(110, t + 0.045);
    oscGain.gain.setValueAtTime(0.12, t);
    oscGain.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
    osc.connect(oscGain); oscGain.connect(this.sfxGain);
    osc.start(t); osc.stop(t + 0.06);
  }

  playEnemyShoot() {
    if (!this.initialized) return;
    this.resume();
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    osc.connect(gain); gain.connect(this.sfxGain);
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(440, this.audioContext.currentTime);
    osc.frequency.exponentialRampToValueAtTime(110, this.audioContext.currentTime + 0.15);
    gain.gain.setValueAtTime(0.2, this.audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.15);
    osc.start(); osc.stop(this.audioContext.currentTime + 0.15);
  }

  playExplosion() {
    if (!this.initialized) return;
    this.resume();
    const bufferSize = this.audioContext.sampleRate * 0.3;
    const buffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const noise = this.audioContext.createBufferSource();
    noise.buffer = buffer;
    const filter = this.audioContext.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(1000, this.audioContext.currentTime);
    filter.frequency.exponentialRampToValueAtTime(100, this.audioContext.currentTime + 0.3);
    const gain = this.audioContext.createGain();
    gain.gain.setValueAtTime(0.5, this.audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);
    noise.connect(filter); filter.connect(gain); gain.connect(this.sfxGain);
    noise.start();
  }

  playJump() {
    if (!this.initialized) return;
    this.resume();
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    osc.connect(gain); gain.connect(this.sfxGain);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(150, this.audioContext.currentTime);
    osc.frequency.exponentialRampToValueAtTime(400, this.audioContext.currentTime + 0.15);
    gain.gain.setValueAtTime(0.2, this.audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.15);
    osc.start(); osc.stop(this.audioContext.currentTime + 0.15);
  }

  playHit() {
    if (!this.initialized) return;
    this.resume();
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    osc.connect(gain); gain.connect(this.sfxGain);
    osc.type = 'square';
    osc.frequency.setValueAtTime(200, this.audioContext.currentTime);
    osc.frequency.setValueAtTime(100, this.audioContext.currentTime + 0.05);
    gain.gain.setValueAtTime(0.3, this.audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.1);
    osc.start(); osc.stop(this.audioContext.currentTime + 0.1);
  }

  playPowerUp() {
    if (!this.initialized) return;
    this.resume();
    [523.25, 659.25, 783.99, 1046.50].forEach((freq, i) => {
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      osc.connect(gain); gain.connect(this.sfxGain);
      osc.type = 'sine'; osc.frequency.value = freq;
      const t = this.audioContext.currentTime + i * 0.08;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.2, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.01, t + 0.15);
      osc.start(t); osc.stop(t + 0.15);
    });
  }

  playGameOver() {
    if (!this.initialized) return;
    this.resume();
    [392, 349.23, 329.63, 293.66].forEach((freq, i) => {
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      osc.connect(gain); gain.connect(this.sfxGain);
      osc.type = 'triangle'; osc.frequency.value = freq;
      const t = this.audioContext.currentTime + i * 0.2;
      gain.gain.setValueAtTime(0.3, t);
      gain.gain.exponentialRampToValueAtTime(0.01, t + 0.3);
      osc.start(t); osc.stop(t + 0.3);
    });
  }

  playLevelUp() {
    if (!this.initialized) return;
    this.resume();
    [261.63, 329.63, 392, 523.25, 659.25].forEach((freq, i) => {
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      osc.connect(gain); gain.connect(this.sfxGain);
      osc.type = 'square'; osc.frequency.value = freq;
      const t = this.audioContext.currentTime + i * 0.1;
      gain.gain.setValueAtTime(0.15, t);
      gain.gain.exponentialRampToValueAtTime(0.01, t + 0.2);
      osc.start(t); osc.stop(t + 0.2);
    });
  }

  playGrenadeThrow() {
    if (!this.initialized) return;
    this.resume();
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    osc.connect(gain); gain.connect(this.sfxGain);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(300, this.audioContext.currentTime);
    osc.frequency.exponentialRampToValueAtTime(600, this.audioContext.currentTime + 0.08);
    osc.frequency.exponentialRampToValueAtTime(200, this.audioContext.currentTime + 0.2);
    gain.gain.setValueAtTime(0.25, this.audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.2);
    osc.start(); osc.stop(this.audioContext.currentTime + 0.2);
  }

  playRocketFire() {
    if (!this.initialized) return;
    this.resume();
    const bufSize = this.audioContext.sampleRate * 0.4;
    const buf = this.audioContext.createBuffer(1, bufSize, this.audioContext.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) d[i] = Math.random() * 2 - 1;
    const n = this.audioContext.createBufferSource(); n.buffer = buf;
    const f = this.audioContext.createBiquadFilter(); f.type = 'bandpass'; f.frequency.value = 800; f.Q.value = 2;
    const g = this.audioContext.createGain();
    g.gain.setValueAtTime(0.4, this.audioContext.currentTime);
    g.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.4);
    n.connect(f); f.connect(g); g.connect(this.sfxGain); n.start();
  }

  playEnemyDeath() {
    if (!this.initialized) return;
    this.resume();
    const t = this.audioContext.currentTime;
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    osc.connect(gain); gain.connect(this.sfxGain);
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(600, t);
    osc.frequency.exponentialRampToValueAtTime(80, t + 0.25);
    gain.gain.setValueAtTime(0.25, t);
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.25);
    osc.start(); osc.stop(t + 0.25);
  }

  playBossPhase() {
    if (!this.initialized) return;
    this.resume();
    const t = this.audioContext.currentTime;
    [200, 250, 300, 400, 500].forEach((freq, i) => {
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      osc.connect(gain); gain.connect(this.sfxGain);
      osc.type = 'sawtooth'; osc.frequency.value = freq;
      const st = t + i * 0.12;
      gain.gain.setValueAtTime(0.25, st);
      gain.gain.exponentialRampToValueAtTime(0.01, st + 0.2);
      osc.start(st); osc.stop(st + 0.2);
    });
  }

  playCountdownBeep() {
    if (!this.initialized) return;
    this.resume();
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    osc.connect(gain); gain.connect(this.sfxGain);
    osc.type = 'sine'; osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.3, this.audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.15);
    osc.start(); osc.stop(this.audioContext.currentTime + 0.15);
  }

  playWeaponSwitch() {
    if (!this.initialized) return;
    this.resume();
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    osc.connect(gain); gain.connect(this.sfxGain);
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(1200, this.audioContext.currentTime);
    osc.frequency.exponentialRampToValueAtTime(800, this.audioContext.currentTime + 0.06);
    gain.gain.setValueAtTime(0.15, this.audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.06);
    osc.start(); osc.stop(this.audioContext.currentTime + 0.06);
  }

  playShieldActivate() {
    if (!this.initialized) return;
    this.resume();
    [400, 600, 800, 1000].forEach((freq, i) => {
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      osc.connect(gain); gain.connect(this.sfxGain);
      osc.type = 'sine'; osc.frequency.value = freq;
      const t = this.audioContext.currentTime + i * 0.05;
      gain.gain.setValueAtTime(0.15, t);
      gain.gain.exponentialRampToValueAtTime(0.01, t + 0.12);
      osc.start(t); osc.stop(t + 0.12);
    });
  }

  playFreeze() {
    if (!this.initialized) return;
    this.resume();
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    osc.connect(gain); gain.connect(this.sfxGain);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(2000, this.audioContext.currentTime);
    osc.frequency.exponentialRampToValueAtTime(400, this.audioContext.currentTime + 0.3);
    gain.gain.setValueAtTime(0.15, this.audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);
    osc.start(); osc.stop(this.audioContext.currentTime + 0.3);
  }

  // === TECHNO MUSIC PLAYLIST ===
  // Each track is a self-contained techno pattern generator

  _clearMusic() {
    this.musicTimeouts.forEach(t => clearTimeout(t));
    this.musicTimeouts = [];
    this.musicNodes.forEach(n => { try { n.stop(); } catch(e) {} });
    this.musicNodes = [];
  }

  _schedOsc(type, freq, startTime, duration, vol = 0.12, dest = null) {
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    osc.connect(gain);
    gain.connect(dest || this.musicGain);
    osc.type = type;
    if (typeof freq === 'number') {
      osc.frequency.value = freq;
    } else {
      // freq is an object with automation: {start, end}
      osc.frequency.setValueAtTime(freq.start, startTime);
      osc.frequency.exponentialRampToValueAtTime(freq.end, startTime + duration);
    }
    gain.gain.setValueAtTime(vol, startTime);
    gain.gain.setValueAtTime(vol, startTime + duration - 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
    osc.start(startTime);
    osc.stop(startTime + duration + 0.05);
    this.musicNodes.push(osc);
    return osc;
  }

  _schedNoise(startTime, duration, vol = 0.1, filterFreq = 8000) {
    const bufSize = Math.floor(this.audioContext.sampleRate * duration);
    const buf = this.audioContext.createBuffer(1, bufSize, this.audioContext.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) d[i] = Math.random() * 2 - 1;
    const src = this.audioContext.createBufferSource(); src.buffer = buf;
    const f = this.audioContext.createBiquadFilter(); f.type = 'highpass'; f.frequency.value = filterFreq;
    const g = this.audioContext.createGain();
    g.gain.setValueAtTime(vol, startTime);
    g.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
    src.connect(f); f.connect(g); g.connect(this.musicGain);
    src.start(startTime);
    this.musicNodes.push(src);
  }

  _schedKick(startTime, vol = 0.2) {
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    osc.connect(gain); gain.connect(this.musicGain);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(150, startTime);
    osc.frequency.exponentialRampToValueAtTime(40, startTime + 0.15);
    gain.gain.setValueAtTime(vol, startTime);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.2);
    osc.start(startTime); osc.stop(startTime + 0.25);
    this.musicNodes.push(osc);
  }

  // Track 1: Dark Techno (levels 1-2) - 128bpm, minor key, rolling bass
  _playTrack1() {
    const bpm = 128; const beat = 60 / bpm;
    const barLen = beat * 4;
    const bassNotes = [55, 55, 65.41, 55, 55, 73.42, 55, 61.74]; // A1, C2, D2, Bb1
    const arpNotes = [220, 261.63, 293.66, 349.23, 293.66, 261.63]; // Am arp

    const playBar = () => {
      if (!this.musicPlaying || this.currentTrack !== 0) return;
      const t = this.audioContext.currentTime;
      // Kick on every beat
      for (let i = 0; i < 4; i++) this._schedKick(t + i * beat, 0.18);
      // Hi-hat on offbeats
      for (let i = 0; i < 8; i++) this._schedNoise(t + i * beat * 0.5, 0.04, 0.04, 9000);
      // Closed hat 16ths
      for (let i = 0; i < 16; i++) this._schedNoise(t + i * beat * 0.25, 0.02, 0.02, 12000);
      // Rolling bass
      bassNotes.forEach((n, i) => {
        this._schedOsc('sawtooth', n, t + i * beat * 0.5, beat * 0.45, 0.12);
      });
      // Arp stabs
      arpNotes.forEach((n, i) => {
        this._schedOsc('square', n, t + i * beat * 0.33, beat * 0.15, 0.05);
      });
      this.musicTimeouts.push(setTimeout(playBar, barLen * 2 * 1000));
    };
    playBar();
  }

  // Track 2: Acid Techno (levels 3-4) - 130bpm, squelchy 303-style bass
  _playTrack2() {
    const bpm = 130; const beat = 60 / bpm;
    const barLen = beat * 4;
    const acidNotes = [110, 130.81, 110, 146.83, 110, 164.81, 130.81, 110];

    const playBar = () => {
      if (!this.musicPlaying || this.currentTrack !== 1) return;
      const t = this.audioContext.currentTime;
      for (let i = 0; i < 4; i++) this._schedKick(t + i * beat, 0.2);
      for (let i = 0; i < 8; i++) this._schedNoise(t + (i + 0.5) * beat * 0.5, 0.03, 0.05, 10000);
      // Acid bass - sawtooth with glide
      acidNotes.forEach((n, i) => {
        const st = t + i * beat * 0.5;
        const osc = this.audioContext.createOscillator();
        const filt = this.audioContext.createBiquadFilter();
        const gain = this.audioContext.createGain();
        osc.connect(filt); filt.connect(gain); gain.connect(this.musicGain);
        osc.type = 'sawtooth'; osc.frequency.value = n;
        filt.type = 'lowpass'; filt.Q.value = 12;
        filt.frequency.setValueAtTime(2000, st);
        filt.frequency.exponentialRampToValueAtTime(200, st + beat * 0.4);
        gain.gain.setValueAtTime(0.14, st);
        gain.gain.exponentialRampToValueAtTime(0.001, st + beat * 0.45);
        osc.start(st); osc.stop(st + beat * 0.5);
        this.musicNodes.push(osc);
      });
      // Clap on 2 and 4
      this._schedNoise(t + beat, 0.08, 0.08, 3000);
      this._schedNoise(t + beat * 3, 0.08, 0.08, 3000);
      this.musicTimeouts.push(setTimeout(playBar, barLen * 1000));
    };
    playBar();
  }

  // Track 3: Hard Techno (levels 5-6) - 135bpm, distorted kick, industrial
  _playTrack3() {
    const bpm = 135; const beat = 60 / bpm;
    const barLen = beat * 4;
    const bassLine = [55, 0, 55, 65.41, 0, 55, 73.42, 55];

    const playBar = () => {
      if (!this.musicPlaying || this.currentTrack !== 2) return;
      const t = this.audioContext.currentTime;
      // Heavy kicks
      for (let i = 0; i < 4; i++) this._schedKick(t + i * beat, 0.25);
      // Offbeat kicks (double kick pattern)
      this._schedKick(t + beat * 0.5, 0.12);
      this._schedKick(t + beat * 2.5, 0.12);
      // Industrial clang
      for (let i = 0; i < 4; i++) {
        this._schedOsc('square', {start: 4000, end: 200}, t + i * beat + beat * 0.25, 0.03, 0.04);
      }
      // Hats
      for (let i = 0; i < 16; i++) this._schedNoise(t + i * beat * 0.25, 0.02, 0.03, 11000);
      // Bass stabs
      bassLine.forEach((n, i) => {
        if (n > 0) this._schedOsc('sawtooth', n, t + i * beat * 0.5, beat * 0.35, 0.13);
      });
      this.musicTimeouts.push(setTimeout(playBar, barLen * 1000));
    };
    playBar();
  }

  // Track 4: Minimal Techno (levels 7-8) - 124bpm, sparse, hypnotic
  _playTrack4() {
    const bpm = 124; const beat = 60 / bpm;
    const barLen = beat * 4;
    const percNotes = [800, 0, 600, 0, 800, 400, 0, 600];

    const playBar = () => {
      if (!this.musicPlaying || this.currentTrack !== 3) return;
      const t = this.audioContext.currentTime;
      // Soft kick
      for (let i = 0; i < 4; i++) this._schedKick(t + i * beat, 0.15);
      // Sparse hat
      this._schedNoise(t + beat * 0.5, 0.03, 0.03, 10000);
      this._schedNoise(t + beat * 1.5, 0.03, 0.03, 10000);
      this._schedNoise(t + beat * 2.5, 0.03, 0.03, 10000);
      this._schedNoise(t + beat * 3.5, 0.03, 0.03, 10000);
      // Minimal perc tones
      percNotes.forEach((n, i) => {
        if (n > 0) this._schedOsc('triangle', n, t + i * beat * 0.5, 0.08, 0.04);
      });
      // Sub bass drone
      this._schedOsc('sine', 55, t, barLen - 0.05, 0.1);
      this.musicTimeouts.push(setTimeout(playBar, barLen * 1000));
    };
    playBar();
  }

  // Track 5: Driving Techno (levels 9-10) - 132bpm, pulsing synth, energetic
  _playTrack5() {
    const bpm = 132; const beat = 60 / bpm;
    const barLen = beat * 4;
    const synthNotes = [329.63, 392, 440, 392, 329.63, 293.66, 329.63, 392]; // E4-G4-A4 pattern

    const playBar = () => {
      if (!this.musicPlaying || this.currentTrack !== 4) return;
      const t = this.audioContext.currentTime;
      for (let i = 0; i < 4; i++) this._schedKick(t + i * beat, 0.2);
      for (let i = 0; i < 8; i++) this._schedNoise(t + (i + 0.5) * beat * 0.5, 0.03, 0.04, 10000);
      // Clap 2 & 4
      this._schedNoise(t + beat, 0.06, 0.06, 4000);
      this._schedNoise(t + beat * 3, 0.06, 0.06, 4000);
      // Pulsing synth
      synthNotes.forEach((n, i) => {
        this._schedOsc('square', n, t + i * beat * 0.5, beat * 0.2, 0.06);
      });
      // Rolling bass
      this._schedOsc('sawtooth', 73.42, t, beat * 2 - 0.02, 0.1);
      this._schedOsc('sawtooth', 82.41, t + beat * 2, beat * 2 - 0.02, 0.1);
      this.musicTimeouts.push(setTimeout(playBar, barLen * 1000));
    };
    playBar();
  }

  // Track 6: Melodic Techno (levels 11-14) - 126bpm, atmospheric pads
  _playTrack6() {
    const bpm = 126; const beat = 60 / bpm;
    const barLen = beat * 4;
    const padChord = [220, 277.18, 329.63]; // Am chord
    const melNotes = [440, 523.25, 587.33, 523.25, 440, 392, 440, 0];

    const playBar = () => {
      if (!this.musicPlaying || this.currentTrack !== 5) return;
      const t = this.audioContext.currentTime;
      for (let i = 0; i < 4; i++) this._schedKick(t + i * beat, 0.16);
      for (let i = 0; i < 8; i++) this._schedNoise(t + (i + 0.5) * beat * 0.5, 0.04, 0.03, 9000);
      // Pad chord
      padChord.forEach(n => {
        this._schedOsc('sine', n, t, barLen - 0.1, 0.04);
        this._schedOsc('triangle', n * 2, t, barLen - 0.1, 0.02);
      });
      // Melody
      melNotes.forEach((n, i) => {
        if (n > 0) this._schedOsc('sine', n, t + i * beat * 0.5, beat * 0.4, 0.05);
      });
      // Sub bass
      this._schedOsc('sine', 55, t, barLen - 0.05, 0.08);
      this.musicTimeouts.push(setTimeout(playBar, barLen * 1000));
    };
    playBar();
  }

  // Track 7: Peak Time Techno (levels 15-18) - 138bpm, intense, relentless
  _playTrack7() {
    const bpm = 138; const beat = 60 / bpm;
    const barLen = beat * 4;
    const riffNotes = [110, 130.81, 110, 146.83, 164.81, 146.83, 130.81, 110];

    const playBar = () => {
      if (!this.musicPlaying || this.currentTrack !== 6) return;
      const t = this.audioContext.currentTime;
      // Pounding kick every beat + ghost kicks
      for (let i = 0; i < 4; i++) this._schedKick(t + i * beat, 0.22);
      for (let i = 0; i < 8; i++) this._schedKick(t + i * beat * 0.5 + beat * 0.25, 0.06);
      // Rapid hats
      for (let i = 0; i < 16; i++) this._schedNoise(t + i * beat * 0.25, 0.02, 0.04, 12000);
      // Open hat offbeats
      for (let i = 0; i < 4; i++) this._schedNoise(t + i * beat + beat * 0.5, 0.1, 0.05, 7000);
      // Riff
      riffNotes.forEach((n, i) => {
        this._schedOsc('sawtooth', n, t + i * beat * 0.5, beat * 0.35, 0.1);
      });
      // Stab
      this._schedOsc('square', 440, t + beat * 1.75, 0.05, 0.06);
      this._schedOsc('square', 440, t + beat * 3.75, 0.05, 0.06);
      this.musicTimeouts.push(setTimeout(playBar, barLen * 1000));
    };
    playBar();
  }

  // Track 8: Boss Battle Techno (levels 19-20) - 140bpm, chaos, heavy
  _playTrack8() {
    const bpm = 140; const beat = 60 / bpm;
    const barLen = beat * 4;

    const playBar = () => {
      if (!this.musicPlaying || this.currentTrack !== 7) return;
      const t = this.audioContext.currentTime;
      // Relentless kick
      for (let i = 0; i < 8; i++) this._schedKick(t + i * beat * 0.5, 0.22);
      // Crash hats
      for (let i = 0; i < 16; i++) this._schedNoise(t + i * beat * 0.25, 0.04, 0.05, 8000);
      // Clap buildup
      for (let i = 0; i < 4; i++) this._schedNoise(t + i * beat, 0.08, 0.07, 3000);
      // Menacing bass
      this._schedOsc('sawtooth', 41.2, t, beat * 2, 0.15); // E1
      this._schedOsc('sawtooth', 46.25, t + beat * 2, beat, 0.15); // F#1
      this._schedOsc('sawtooth', 36.71, t + beat * 3, beat, 0.15); // D1
      // Alarm synth
      this._schedOsc('square', {start: 880, end: 440}, t, beat * 2, 0.04);
      this._schedOsc('square', {start: 440, end: 880}, t + beat * 2, beat * 2, 0.04);
      // Impact noise
      this._schedNoise(t, 0.15, 0.08, 1000);
      this.musicTimeouts.push(setTimeout(playBar, barLen * 1000));
    };
    playBar();
  }

  // Pick track based on level
  _trackForLevel(level) {
    if (level <= 2) return 0;
    if (level <= 4) return 1;
    if (level <= 6) return 2;
    if (level <= 8) return 3;
    if (level <= 10) return 4;
    if (level <= 14) return 5;
    if (level <= 18) return 6;
    return 7;
  }

  startMusic(level = 1) {
    if (!this.initialized) return;
    this.resume();
    const track = this._trackForLevel(level);
    // Don't restart if same track
    if (this.musicPlaying && this.currentTrack === track) return;
    this._clearMusic();
    this.musicPlaying = true;
    this.currentTrack = track;
    const players = [
      () => this._playTrack1(), () => this._playTrack2(),
      () => this._playTrack3(), () => this._playTrack4(),
      () => this._playTrack5(), () => this._playTrack6(),
      () => this._playTrack7(), () => this._playTrack8(),
    ];
    players[track]();
  }

  stopMusic() {
    this.musicPlaying = false;
    this.currentTrack = -1;
    this._clearMusic();
  }

  setMasterVolume(value) {
    if (this.masterGain) this.masterGain.gain.value = Math.max(0, Math.min(1, value));
    this.muted = value === 0;
  }
  setMusicVolume(value) {
    if (this.musicGain) this.musicGain.gain.value = Math.max(0, Math.min(1, value));
  }
  setSfxVolume(value) {
    if (this.sfxGain) this.sfxGain.gain.value = Math.max(0, Math.min(1, value));
  }
}

const soundManager = new SoundManager();
export default soundManager;
